import { format } from 'date-fns';
import { AsYouType, CountryCode } from 'libphonenumber-js';

import { amountToFloat, capitalize } from '@freelancelabs/utils';

import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../constants';
import { dateLocale, localeCode } from '../i18n';
import { Amount } from '../types';

interface FormatAddressArgs {
    street?: string | undefined | null;
    city?: string | undefined | null;
    country?: string | undefined | null;
    postalCode?: string | undefined | null;
}

export const formatAddress = ({ street, postalCode, city, country }: FormatAddressArgs) => {
    return `${capitalize(street)} ${postalCode ?? ''} ${capitalize(city)}${country ? `, ${capitalize(country)}` : ''}`;
};

interface FormatCurrency {
    currency?: string;
    maximumFractionDigits?: number;
    amount: number | bigint | Amount;
    notation?: 'compact' | 'standard' | 'scientific' | 'engineering';
    signDisplay?: 'auto' | 'always' | 'exceptZero' | 'never';
}

export const formatMoney = ({
    currency = DEFAULT_CURRENCY,
    amount,
    maximumFractionDigits,
    notation,
    signDisplay,
}: FormatCurrency): string => {
    const config: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        maximumFractionDigits,
        notation,
        signDisplay,
    };
    let value;
    if (typeof amount === 'number' || typeof amount === 'bigint') {
        value = amount;
    } else {
        value = amountToFloat(amount);
    }

    return new Intl.NumberFormat(localeCode, config).format(value);
};

interface FormatNumberConfig {
    maximumFractionDigits?: number;
    signDisplay?: 'auto' | 'always' | 'exceptZero' | 'never';
    notation?: 'compact' | 'standard' | 'scientific' | 'engineering';
}

const FORMAT_NUMBER_DEFAULT_CONFIG: FormatNumberConfig = {
    maximumFractionDigits: 0,
    signDisplay: 'auto',
    notation: 'standard',
};

export const formatNumber = (number: number, config: FormatNumberConfig = FORMAT_NUMBER_DEFAULT_CONFIG): string => {
    const mergedConfig: Intl.NumberFormatOptions = { style: 'decimal', ...config };
    return new Intl.NumberFormat(localeCode, mergedConfig).format(number);
};

export const formatPercent = (number: number, config: FormatNumberConfig = FORMAT_NUMBER_DEFAULT_CONFIG): string => {
    const mergedConfig: Intl.NumberFormatOptions = { style: 'percent', ...config };
    return new Intl.NumberFormat(localeCode, mergedConfig).format(number);
};

const differenceIncrease = (oldValue: number, newValue: number) => {
    if (oldValue === 0) {
        return newValue;
    }
    if (newValue === 0) {
        return -oldValue;
    }
    return (newValue - oldValue) / oldValue;
};

export const formatPercentIncrease = (oldValue: number, newValue: number) =>
    formatPercent(differenceIncrease(oldValue, newValue), {
        signDisplay: 'always',
    });

interface FormatDate {
    date: Date;
    dateStyle?: 'full' | 'long' | 'medium' | 'short';
    timeStyle?: 'full' | 'long' | 'medium' | 'short';
}

export const formatDate = ({ date, dateStyle, timeStyle }: FormatDate): string => {
    const config = { dateStyle, timeStyle };
    return new Intl.DateTimeFormat(localeCode, config).format(date);
};

/**
 * Un MOIS et son année, sans le jour : « février 2024 ».
 *
 * **Il est à côté de `formatDate` et non dedans**, parce que `dateStyle` et les champs
 * individuels de `Intl.DateTimeFormat` s'excluent : les mélanger dans une même signature
 * offrirait des combinaisons que l'API native refuse à l'exécution.
 *
 * Il existe pour les périodes mensuelles, où le jour est du bruit ou pire : la date de
 * réalisation d'un relevé d'activité est le dernier jour du mois, et l'afficher se lirait
 * comme le jour où le travail a eu lieu, ce qu'elle ne dit pas.
 */
export const formatMonth = ({ date }: { date: Date }): string =>
    new Intl.DateTimeFormat(localeCode, { month: 'long', year: 'numeric' }).format(date);

export const formatPhoneNumber = (phoneNumber: string): string => {
    const defaultCountryCode = (dateLocale?.code || DEFAULT_LOCALE).slice(0, 2).toUpperCase() as CountryCode;

    return new AsYouType(defaultCountryCode).input(phoneNumber);
};

export const formatMonthYear = (date: number | string | Date) =>
    capitalize(format(new Date(date), 'MMMM yyyy', { locale: dateLocale }));

export const formatMonthShort = (date: number | string | Date) =>
    capitalize(format(new Date(date), 'LLL', { locale: dateLocale }));

export const formatYear = (date: Date) => new Intl.DateTimeFormat(localeCode, { year: 'numeric' }).format(date);

export const formatList = (items: string[]): string => {
    return new Intl.ListFormat(localeCode).format(items);
};
